import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert } from 'reactstrap';
import { createMsalInstance } from '../../lib/authentication/entra/msalConfig';
import { getItem } from '../../utils/localStorage';
import Loader from '../common/loader';
import entityComposition from '../common/classes/entityComposition';
import store from '../../reducers/storeRegistration';

/**
 * Generate SHA-256 hash of a string using Web Crypto API
 * This provides cryptographic security for hashing sensitive data
 *
 * @param {string} str - String to hash
 * @returns {Promise<string>} SHA-256 hash as hexadecimal string
 */
const sha256Hash = async str => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    // Fallback: if crypto API unavailable (e.g., test environment), use simple hash
    // eslint-disable-next-line no-console
    console.warn('Crypto API unavailable, using fallback hash:', error);
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
      const char = str.charCodeAt(i);
      // Use multiplication instead of bitwise left shift (hash * 32 = hash << 5)
      hash = hash * 32 - hash + char;
      // Keep hash within safe integer range using modulo instead of bitwise AND
      hash %= 2147483647;
    }
    return Math.abs(hash).toString(16);
  }
};

/**
 * Generate a unique key for tracking processed authorization codes
 * Uses SHA-256 hash of the authorization code to ensure uniqueness while avoiding
 * storing the sensitive credential in browser storage
 * This prevents replay attacks by ensuring each unique code is tracked separately
 * while maintaining security by not storing the actual authorization code
 *
 * @param {string} code - The authorization code from OAuth callback
 * @returns {Promise<string|null>} Storage key for tracking processed codes,
 *   or null if code is empty
 */
const getProcessedCodeKey = async code => {
  if (!code) return null;
  // Hash the code using SHA-256 to avoid storing sensitive credentials in sessionStorage
  // This maintains uniqueness while protecting the authorization code
  // Critical for security - prevents extraction of codes if sessionStorage is compromised
  const codeHash = await sha256Hash(code);
  return `entra_callback_code_processed_${codeHash}`;
};

/**
 * Synchronously check and mark a code as processed using a temporary key
 * This provides immediate race condition protection while SHA-256 hash is computed
 * The temporary key is replaced with the SHA-256 hash once computed
 *
 * @param {string} code - The authorization code
 * @returns {string} Temporary storage key for immediate check
 */
const getTempProcessedCodeKey = code => {
  if (!code) return null;
  // Use a simple synchronous hash for immediate race condition check
  // This will be replaced with SHA-256 hash once computed
  let hash = 0;
  for (let i = 0; i < code.length; i += 1) {
    const char = code.charCodeAt(i);
    // Use multiplication instead of bitwise left shift (hash * 32 = hash << 5)
    hash = hash * 32 - hash + char;
    // Keep hash within safe integer range using modulo instead of bitwise AND
    hash %= 2147483647;
  }
  return `entra_callback_code_processed_temp_${Math.abs(hash).toString(16)}`;
};
const EntraCallback = ({
  login,
  loginActions: loginActionsProp
}) => {
  const history = useHistory();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Note: hasProcessed ref removed - using code-specific sessionStorage checks instead
  const tokenExchangeStarted = useRef(false);
  const hasRedirected = useRef(false);
  useEffect(() => {
    // DEDUPLICATION STRATEGY:
    // We use a code-specific check (SHA-256 hash of authorization code) as the
    // single source of truth for preventing duplicate processing. This ensures:
    // 1. Each unique authorization code can only be processed once (prevents replay attacks)
    // 2. Different codes don't interfere with each other
    // 3. The sensitive authorization code is never stored in sessionStorage (only its hash)
    //
    // Flow:
    // 1. Extract authorization code from URL or stored params
    // 2. Check if this specific code was already processed (using hash)
    // 3. If processed, exit early to prevent duplicate token exchange
    // 4. If not processed, mark as processed and continue

    // Extract authorization code from URL or stored params

    const urlHash = window.location.hash || '';
    const urlSearch = window.location.search || '';
    let callbackCode = null;

    // Try to extract code from URL first
    if (urlHash.includes('code=')) {
      const hashMatch = urlHash.match(/code=([^&]+)/);
      if (hashMatch) {
        callbackCode = hashMatch[1];
      }
    } else if (urlSearch.includes('code=')) {
      const searchMatch = urlSearch.match(/code=([^&]+)/);
      if (searchMatch) {
        callbackCode = searchMatch[1];
      }
    }

    // If not in URL, check stored params (from authCallback.html)
    if (!callbackCode) {
      const storedParamsStr = sessionStorage.getItem('entra_callback_params');
      if (storedParamsStr) {
        try {
          var _storedParams$hashPar, _storedParams$queryPa;
          const storedParams = JSON.parse(storedParamsStr);
          callbackCode = ((_storedParams$hashPar = storedParams.hashParams) === null || _storedParams$hashPar === void 0 ? void 0 : _storedParams$hashPar.code) || ((_storedParams$queryPa = storedParams.queryParams) === null || _storedParams$queryPa === void 0 ? void 0 : _storedParams$queryPa.code);
        } catch (_unused) {
          // Ignore parse errors
        }
      }
    }

    // Check if user is already authenticated (prevents processing if already logged in)
    let userAlreadyAuthenticated = false;
    try {
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.access_token) {
          userAlreadyAuthenticated = true;
        }
      }
    } catch (_unused2) {
      // Ignore parse errors
    }
    if (userAlreadyAuthenticated) {
      setLoading(false);
      if (window.location.hash.includes('/auth/callback')) {
        window.history.replaceState({}, '', '/#/');
      }
      history.replace('/');
      return;
    }

    // Code-specific duplicate processing check
    // This is the primary deduplication mechanism - checks if THIS specific code was processed
    if (callbackCode) {
      // First, use a temporary synchronous key for immediate race condition protection
      const tempKey = getTempProcessedCodeKey(callbackCode);
      const codeAlreadyProcessed = sessionStorage.getItem(tempKey);
      if (codeAlreadyProcessed) {
        // Code was already processed - exit early to prevent duplicate processing
        // This prevents replay attacks and duplicate token exchanges
        setLoading(false);
        history.replace('/');
        return;
      }
      // Mark as processed IMMEDIATELY to prevent duplicate processing
      // This must happen synchronously before any async operations
      sessionStorage.setItem(tempKey, 'true');

      // Then, replace with SHA-256 hash for security (async, but doesn't block)
      // This prevents storing the sensitive authorization code in sessionStorage
      (async () => {
        const processedCodeKey = await getProcessedCodeKey(callbackCode);
        if (processedCodeKey && tempKey) {
          // Migrate from temp key to secure SHA-256 key
          const wasProcessed = sessionStorage.getItem(tempKey);
          if (wasProcessed) {
            sessionStorage.removeItem(tempKey);
            sessionStorage.setItem(processedCodeKey, 'true');
          }
        }
      })();
    }
    // Note: If no code is found, we still proceed to handleCallback()
    // This allows token refresh from cache scenarios where there's no redirect code
    // The code check above only prevents duplicate processing when a code exists

    const handleCallback = async () => {
      try {
        // Get customer settings from sessionStorage (stored when login started)
        // Hardcoded customer settings are in loginFormContainer.jsx
        // Keep settings in sessionStorage for token refresh flows
        let customerSettings = null;
        const storedSettingsStr = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('entra_customer_settings') : null;
        if (storedSettingsStr) {
          try {
            customerSettings = JSON.parse(storedSettingsStr);
            // Don't remove - keep for token refresh flows
          } catch (_unused3) {
            // Ignore parse errors
          }
        }

        // Create MSAL instance dynamically using customer settings
        const msalInstance = customerSettings ? createMsalInstance(customerSettings) : null;
        if (!msalInstance) {
          setError('SSO sign-in is not available. Please refresh the page.');
          setLoading(false);
          return;
        }

        // Ensure MSAL is initialized
        await msalInstance.initialize();

        // Check if parameters were stored by authCallback.html (from path-based URL)
        const storedParamsStr = sessionStorage.getItem('entra_callback_params');
        let storedParams = null;
        if (storedParamsStr) {
          try {
            storedParams = JSON.parse(storedParamsStr);

            // Restore URL parameters so MSAL can process them
            if (storedParams.query || storedParams.hash) {
              const restoreUrl = window.location.pathname + (storedParams.query || '') + (storedParams.hash || '');
              window.history.replaceState({}, '', restoreUrl);
            }
            // Remove after use
            sessionStorage.removeItem('entra_callback_params');
          } catch (_unused4) {
            // Ignore parse errors
          }
        }

        // Handle redirect promise - MSAL will process the redirect response
        // Note: If MSAL already processed the redirect during initialization (from path-based URL),
        // this will return null, but the account will be in MSAL's cache
        const response = await msalInstance.handleRedirectPromise();
        let idToken = null;
        if (response) {
          // MSAL just processed the redirect - get idToken from response
          ({
            idToken
          } = response);
        } else {
          // MSAL already processed the redirect during initialization
          // Get the active account from MSAL's cache
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            // Get the idToken from the account's idTokenClaims or acquire silently
            const [account] = accounts;
            // Try to get idToken from cache or acquire silently
            try {
              const silentResponse = await msalInstance.acquireTokenSilent({
                scopes: ['openid', 'profile', 'email'],
                account
              });
              ({
                idToken
              } = silentResponse);
            } catch (_unused5) {
              // If silent acquisition fails, try to get idToken from sessionStorage
              // MSAL stores the idToken in sessionStorage
              const {
                auth: {
                  clientId
                }
              } = msalInstance.getConfiguration();
              // MSAL stores tokens with keys like: msal.{clientId}.idtoken.{account.homeAccountId}
              let idTokenKey = null;
              for (let i = 0; i < sessionStorage.length; i += 1) {
                const key = sessionStorage.key(i);
                if (key && key.includes(clientId) && key.includes('idtoken') && key.includes(account.homeAccountId)) {
                  idTokenKey = key;
                  break;
                }
              }
              if (idTokenKey) {
                const tokenValue = sessionStorage.getItem(idTokenKey);
                // MSAL stores tokens as JSON strings, so we need to parse it
                try {
                  const tokenData = JSON.parse(tokenValue);
                  // Fallback chain for extracting idToken from MSAL storage:
                  // 1. tokenData.secret - MSAL v2+ stores the token in 'secret' property
                  // 2. tokenData.idToken - Some MSAL versions store it directly as 'idToken'
                  // 3. tokenValue - Fallback to raw value if structure is unexpected
                  // This handles different MSAL library versions and storage formats
                  idToken = tokenData.secret || tokenData.idToken || tokenValue;
                } catch (_unused6) {
                  // If it's not JSON, use it directly
                  idToken = tokenValue;
                }
              }
            }
          }
        }
        if (!idToken) {
          setError('No authentication token received. Please try logging in again.');
          setLoading(false);
          return;
        }

        // Get customer from the MSAL response state parameter (passed during login redirect)
        // Fallback to localStorage or empty string
        let customer = '';
        if (response && response.state) {
          customer = response.state;
        } else {
          // Fallback to localStorage if state not available
          customer = getItem('customer') || '';
        }

        // Customer should always be present since it's required before showing SSO button
        // However, handle gracefully if missing (shouldn't happen in normal flow)
        if (!customer) {
          setError('Customer name is required. Please try logging in again.');
          setLoading(false);
          return;
        }

        // Exchange Entra ID token for our JWT token
        tokenExchangeStarted.current = true;
        try {
          // Call serverEntraLogin and wait for it to complete
          await loginActionsProp.serverEntraLogin(customer, idToken);
        } catch (loginErr) {
          // Handle unexpected errors (e.g. network, thrown from serverEntraLogin) before Redux
          // eslint-disable-next-line no-console
          console.error('SSO login failed:', loginErr);
          setError((loginErr === null || loginErr === void 0 ? void 0 : loginErr.message) || 'Unable to complete login. Please try again.');
          setLoading(false);
          tokenExchangeStarted.current = false;
          return;
        }

        // Immediately check Redux state after dispatch completes
        // Redux dispatches are synchronous, so the state should be updated immediately
        const currentState = store.getState();
        const currentLogin = currentState.login;

        // Force redirect immediately if authentication succeeded
        if (currentLogin.isAuthenticated && !hasRedirected.current) {
          hasRedirected.current = true;
          setLoading(false);
          // Clear callback params from URL immediately to prevent React Router rematching

          if (window.location.hash.includes('/auth/callback')) {
            window.history.replaceState({}, '', '/#/');
          }
          // Redirect immediately - flag stays set to prevent reprocessing on page reload
          // Flag cleared when user starts new login flow (in loginForm component)
          history.replace('/');
        } else if (currentLogin.error && !hasRedirected.current) {
          setError(currentLogin.error || 'Authentication failed. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        var _err$message;
        // Handle state_mismatch error specifically - this means the OAuth state doesn't match
        // This can happen if the callback is retried after MSAL state was cleared
        const isStateMismatch = err.errorCode === 'state_mismatch' || ((_err$message = err.message) === null || _err$message === void 0 ? void 0 : _err$message.includes('state_mismatch'));
        if (isStateMismatch) {
          // Clear all MSAL-related state from sessionStorage
          try {
            const msalKeys = [];
            for (let i = 0; i < sessionStorage.length; i += 1) {
              const key = sessionStorage.key(i);
              if (key && (key.includes('msal.') || key.includes('login.microsoftonline.com'))) {
                msalKeys.push(key);
              }
            }
            msalKeys.forEach(key => sessionStorage.removeItem(key));
          } catch (_unused7) {
            // Ignore clear errors
          }

          // Clear code-specific processed flags to allow retry
          // Note: We only clear code-specific flags, not general flags (consolidated deduplication)
          if (callbackCode) {
            // Clear both temp key (synchronous) and SHA-256 key (async)
            const tempKey = getTempProcessedCodeKey(callbackCode);
            if (tempKey) {
              sessionStorage.removeItem(tempKey);
            }
            // Also clear SHA-256 key if it exists (async, but doesn't block)
            (async () => {
              const processedCodeKey = await getProcessedCodeKey(callbackCode);
              if (processedCodeKey) {
                sessionStorage.removeItem(processedCodeKey);
              }
            })();
          }

          // Clear callback params
          sessionStorage.removeItem('entra_callback_params');

          // Show user-friendly error and redirect to login
          setError('Authentication session expired. Please try logging in again.');
          setLoading(false);

          // Redirect to login page after a short delay
          setTimeout(() => {
            window.history.replaceState({}, '', '/#/');
            history.replace('/');
          }, 2000);
          return;
        }
        // Handle other errors
        const errorMessage = err.message || err.errorMessage || 'Authentication failed';
        setError(errorMessage);
        setLoading(false);
        // Clear code-specific processed flags on error so user can retry
        // Note: We only clear code-specific flags, not general flags (consolidated deduplication)
        if (callbackCode) {
          // Clear both temp key (synchronous) and SHA-256 key (async)
          const tempKey = getTempProcessedCodeKey(callbackCode);
          if (tempKey) {
            sessionStorage.removeItem(tempKey);
          }
          // Also clear SHA-256 key if it exists (async, but doesn't block)
          (async () => {
            const processedCodeKey = await getProcessedCodeKey(callbackCode);
            if (processedCodeKey) {
              sessionStorage.removeItem(processedCodeKey);
            }
          })();
        }
      }
    };
    handleCallback();
    // Only run once on mount - don't include history/location to prevent re-runs on redirect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for authentication success and redirect when it happens
  useEffect(() => {
    if (!tokenExchangeStarted.current) {
      // Don't handle auth state changes until token exchange has started
      return;
    }
    if (login.isAuthenticated && !hasRedirected.current) {
      // Authentication succeeded - redirect immediately
      hasRedirected.current = true;
      setLoading(false);
      // Use replaceState to avoid adding to history and ensure immediate redirect
      history.replace('/');
    } else if (login.error && !hasRedirected.current) {
      // Authentication failed
      setError(login.error || 'Authentication failed. Please try again.');
      setLoading(false);
    }
    // Only watch login.isAuthenticated and login.error, NOT loading or history
    // Removing loading prevents rerenders when setLoading is called
    // Removing history prevents rerenders when history object changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login.isAuthenticated, login.error]);
  if (loading) {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }
    }, /*#__PURE__*/React.createElement(Loader, {
      loaded: false
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: '20px'
      }
    }, "Completing sign-in..."));
  }
  if (error) {
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }
    }, /*#__PURE__*/React.createElement(Alert, {
      color: "danger",
      style: {
        maxWidth: '500px'
      }
    }, /*#__PURE__*/React.createElement("h4", null, "Authentication Error"), /*#__PURE__*/React.createElement("p", null, error), /*#__PURE__*/React.createElement("button", {
      className: "btn btn-primary",
      onClick: () => history.replace('/'),
      type: "button",
      style: {
        marginTop: '10px'
      }
    }, "Return to Login")));
  }
  return null;
};
EntraCallback.propTypes = {
  login: PropTypes.shape({
    isAuthenticated: PropTypes.bool,
    error: PropTypes.string
  }).isRequired,
  loginActions: PropTypes.shape({
    serverEntraLogin: PropTypes.func.isRequired
  }).isRequired
};
export { EntraCallback as TestEntraCallback };
export default entityComposition(EntraCallback, ['login']);